import React, { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Layout from "@/components/Layout";
import api, { formatApiError } from "@/lib/api";
import { money, pct, shortDate, statusLabel, statusClass, Pill } from "@/lib/money";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { ArrowLeft, FileText, ShieldCheck, Download, CheckCircle2 } from "lucide-react";

export default function LoanAgreement() {
  const { id } = useParams();
  const [app, setApp] = useState(null);
  const [agr, setAgr] = useState(null);
  const [name, setName] = useState("");
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [a, g] = await Promise.all([
        api.get(`/applications/${id}`),
        api.get(`/applications/${id}/agreement`),
      ]);
      setApp(a.data);
      setAgr(g.data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const openPdf = async () => {
    try {
      const r = await api.get(`/applications/${id}/agreement/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      window.open(url, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const sign = async () => {
    setBusy(true);
    try {
      // The hash is sent back with the signature so the server can confirm the
      // terms have not moved since this page was loaded.
      await api.post(`/applications/${id}/sign`, {
        signed_name: name.trim(),
        agree: true,
        document_hash: agr.document_hash,
      });
      toast.success("Agreement signed");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  if (!app || !agr) {
    return <Layout><div className="p-8 text-slate-500">Loading your agreement…</div></Layout>;
  }

  const snap = app.calculator_snapshot || {};
  const signed = agr.signed;
  const costOfBorrowing = Number(snap.interest || 0) + Number(snap.total_fees || 0);

  return (
    <Layout>
      <div className="fade-in space-y-4 max-w-3xl">
        <div>
          <Link to={`/borrower/applications/${id}`}
                className="text-sm text-blue-700 hover:underline inline-flex items-center gap-1">
            <ArrowLeft className="h-3.5 w-3.5" /> Back to application
          </Link>
          <h1 className="text-2xl font-semibold tracking-tight mt-1" style={{ fontFamily: "Outfit" }}>
            Your loan agreement
          </h1>
          <p className="text-sm text-slate-500">
            {app.product_name} · <Pill className={statusClass(app.status)}>{statusLabel(app.status)}</Pill>
          </p>
        </div>

        {/* The disclosure box. This is the part a borrower is entitled to see
            before being bound, and the app previously showed none of it. */}
        <Card className="rounded-xl border-slate-200 overflow-hidden">
          <CardContent className="p-0">
            <div className="px-5 py-3 bg-slate-50 border-b border-slate-100">
              <h2 className="font-semibold" style={{ fontFamily: "Outfit" }}>Cost of borrowing</h2>
              <p className="text-xs text-slate-500">What this loan costs you, before you agree to it.</p>
            </div>
            <div className="p-5 space-y-2 text-sm">
              {[
                ["Amount you receive", money(app.amount)],
                ["Interest", money(snap.interest)],
                ["Fees", money(snap.total_fees)],
              ].map(([k, v]) => (
                <div key={k} className="flex justify-between">
                  <span className="text-slate-500">{k}</span>
                  <span className="tabular-nums">{v}</span>
                </div>
              ))}
              <div className="flex justify-between pt-2 border-t border-slate-200 font-semibold">
                <span>Total cost of borrowing</span>
                <span className="tabular-nums">{money(costOfBorrowing)}</span>
              </div>
              <div className="flex justify-between font-semibold">
                <span>Total you will repay</span>
                <span className="tabular-nums">{money(snap.total_payable)}</span>
              </div>
              <div className="flex justify-between items-center pt-3 mt-1 border-t border-slate-200">
                <div>
                  <div className="font-semibold text-blue-900">Annual percentage rate (APR)</div>
                  <div className="text-xs text-slate-500">
                    The yearly cost of this credit, so you can compare it with other offers.
                  </div>
                </div>
                <span className="text-2xl font-bold text-blue-900 tabular-nums"
                      style={{ fontFamily: "Outfit" }}>
                  {snap.effective_apr != null ? pct(snap.effective_apr, 2) : "—"}
                </span>
              </div>
              <div className="flex justify-between pt-2 text-slate-500">
                <span>{snap.n_payments} payments, {String(app.repayment_frequency || "").replace("_", " ")}</span>
                <span>{money(snap.per_payment)} each</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-xl border-slate-200"><CardContent className="p-5">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              <FileText className="h-5 w-5 text-blue-900" />
              <div>
                <div className="font-medium">Full agreement and disclosure statement</div>
                <div className="text-xs text-slate-500">
                  Read it before you sign. It opens as a PDF you can keep.
                </div>
              </div>
            </div>
            <Button variant="outline" onClick={openPdf} data-testid="open-agreement-pdf">
              <Download className="h-4 w-4 mr-1" /> {signed ? "Signed copy" : "Read the agreement"}
            </Button>
          </div>
        </CardContent></Card>

        {signed ? (
          <Card className="rounded-xl border-emerald-200 bg-emerald-50">
            <CardContent className="p-5">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="h-6 w-6 text-emerald-700 shrink-0" />
                <div className="text-sm">
                  <div className="font-semibold text-emerald-900">
                    Signed by {agr.signature?.signed_name} on {shortDate(agr.signature?.signed_at)}
                  </div>
                  <p className="text-emerald-800 mt-1">
                    Your funds will be released to your nominated account. A copy of the signed
                    agreement is available above at any time.
                  </p>
                  <p className="text-emerald-700/80 mt-2 font-mono text-[11px] break-all">
                    Document reference (SHA-256): {agr.signature?.document_hash}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card className="rounded-xl border-slate-200"><CardContent className="p-5 space-y-4">
            <div className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-blue-900" />
              <h2 className="font-semibold" style={{ fontFamily: "Outfit" }}>Sign electronically</h2>
            </div>
            <div className="flex items-start gap-3">
              <Checkbox id="agree" checked={agree} onCheckedChange={(v) => setAgree(!!v)}
                        data-testid="agree-checkbox" className="mt-0.5" />
              <Label htmlFor="agree" className="text-sm font-normal leading-relaxed cursor-pointer">
                {agr.affirmation}
              </Label>
            </div>
            <div>
              <Label>Type your full legal name to sign</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)}
                     placeholder="Your full legal name as it appears on your ID"
                     className="h-12 text-lg" data-testid="signature-name" />
              <p className="text-xs text-slate-500 mt-1">
                This must match the legal name on your profile. Your name, the time, and your
                IP address are recorded with the signature.
              </p>
            </div>
            <Button onClick={sign} disabled={busy || !agree || name.trim().length < 2}
                    className="w-full h-12 bg-blue-900 hover:bg-blue-800 text-base font-semibold"
                    data-testid="sign-agreement-btn">
              Sign and accept this loan
            </Button>
            <p className="text-xs text-slate-500 text-center">
              Funds are not released until this agreement is signed.
            </p>
          </CardContent></Card>
        )}
      </div>
    </Layout>
  );
}
